import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link, Outlet, useMatch } from "react-router-dom";
import { bauvorhabenApi, adminApi, invoicesApi, dokumenteApi } from "@/services/api";
import { useAuth } from "@/hooks/useAuth";
import { emitAppEvent, useAppEvent } from "@/utils/appEvents";
import type {
  Bauvorhaben, BauvorhabenContact, BauvorhabenContactRole,
  Contact, User, GewerkStatus, AccordionPayload, BauvorhabenRechnungen,
  BauvorhabenDokumente,
} from "@/types";
import { ArrowLeft, Loader2, AlertCircle } from "lucide-react";
import { useOpenContactPanel } from "@/components/sidepanel/registry";
import { BauvorhabenReadView } from "@/components/bauvorhaben/BauvorhabenReadView";
import { BauvorhabenEditForm } from "@/components/bauvorhaben/BauvorhabenEditForm";
import type { BauvorhabenDetailContext } from "@/components/bauvorhaben/BauvorhabenDetailContext";

/**
 * Bauvorhaben-Detail als Layout-Route: besitzt alle Daten und Handler und
 * rendert Leseansicht + Bearbeiten-Spalte (Unter-Route `edit`). Beim
 * Anlegen (/bauvorhaben/new, id === 'new') wird nur das Formular
 * gerendert — keine Leseansicht, keine Assoziationen, kein Outlet.
 */
export function BauvorhabenDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isApprover, hasModule } = useAuth();
  const openContactInPanel = useOpenContactPanel();
  const isNew = id === 'new';
  const editOpen = useMatch('/bauvorhaben/:id/edit') !== null;

  // Kontakt-Links öffnen die Split-View-Seitenleiste statt zu navigieren;
  // Modifikator-Klick / mittlere Maustaste bleibt natives Browser-Verhalten.
  const openContactFromLink = (e: React.MouseEvent, contactId: number, name?: string) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    openContactInPanel(contactId, name);
  };

  const [bauvorhaben, setBauvorhaben] = useState<Bauvorhaben | null>(null);
  const [contacts, setContacts] = useState<BauvorhabenContact[]>([]);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [users, setUsers] = useState<User[]>([]);

  const [formData, setFormData] = useState({
    name: '',
    project_number: '',
    street: '',
    postal_code: '',
    city: '',
    abgebrochen: false,
    start_date: '',
    end_date: '',
    bauleiter_user_id: '',
    notes: '',
  });

  // Vertriebler-Auswahl im Formular: {id, name} oder null (kein Vertriebler)
  const [vertriebler, setVertriebler] = useState<{ id: number; name: string } | null>(null);

  // Gewerk-Checkliste (Akkordeon) — gesamter aktiver Katalog
  const [accordion, setAccordion] = useState<AccordionPayload | null>(null);

  // Finanzen: letzte Rechnungen + Summen (Karte in der Leseansicht)
  const [rechnungen, setRechnungen] = useState<BauvorhabenRechnungen | null>(null);

  // Dokumente-Karte — nur für Nutzer mit beiden Modulen (bauvorhaben +
  // dokumente); ohne dokumente-Modul: keine Karte, kein Request, keine 403.
  const canDokumente = hasModule('dokumente');
  const [dokumente, setDokumente] = useState<BauvorhabenDokumente | null>(null);

  const [associationError, setAssociationError] = useState('');
  // Positive Rückmeldung (z. B. erzeugte Adress-Anträge für Bauherren)
  const [associationInfo, setAssociationInfo] = useState('');

  const loadDetail = useCallback(() => {
    if (isNew) return;
    setIsLoading(true);
    bauvorhabenApi
      .getById(Number(id))
      .then((response) => {
        setBauvorhaben(response.data.data.bauvorhaben);
        setContacts(response.data.data.contacts);
        setFormData({
          name: response.data.data.bauvorhaben.name ?? '',
          project_number: response.data.data.bauvorhaben.project_number ?? '',
          street: response.data.data.bauvorhaben.street ?? '',
          postal_code: response.data.data.bauvorhaben.postal_code ?? '',
          city: response.data.data.bauvorhaben.city ?? '',
          // Der Override überlebt die Status-Derivation im Backend — der
          // Vergleich mit 'abgebrochen' stellt das klebrige Flag korrekt her.
          abgebrochen: response.data.data.bauvorhaben.status === 'abgebrochen',
          start_date: response.data.data.bauvorhaben.start_date ?? '',
          end_date: response.data.data.bauvorhaben.end_date ?? '',
          bauleiter_user_id: response.data.data.bauvorhaben.bauleiter_user_id
            ? String(response.data.data.bauvorhaben.bauleiter_user_id)
            : '',
          notes: response.data.data.bauvorhaben.notes ?? '',
        });
        setVertriebler(
          response.data.data.bauvorhaben.vertriebler_contact_id
            ? {
                id: response.data.data.bauvorhaben.vertriebler_contact_id,
                name: response.data.data.bauvorhaben.vertriebler_name || 'Kontakt',
              }
            : null
        );
      })
      .catch(() => setError('Bauvorhaben nicht gefunden'))
      .finally(() => setIsLoading(false));
  }, [id, isNew]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  // Finanzen-Karte: eigenen Fetch + Reload bei Rechnungs-Änderungen
  // (Statuswechsel/Neuanlage passieren in der Rechnungen-Spalte).
  const [rechnungenReloadKey, setRechnungenReloadKey] = useState(0);
  useAppEvent('rechnungen:changed', () => setRechnungenReloadKey((k) => k + 1));
  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    invoicesApi
      .getByBauvorhaben(Number(id))
      .then((response) => {
        if (!cancelled) setRechnungen(response.data.data);
      })
      .catch(() => {
        if (!cancelled) setRechnungen(null);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew, rechnungenReloadKey]);

  // Dokumente-Karte: eigener Fetch + Reload bei dokumente:changed
  // (Upload/Löschen passieren in der Karte selbst).
  const [dokumenteReloadKey, setDokumenteReloadKey] = useState(0);
  useAppEvent('dokumente:changed', () => setDokumenteReloadKey((k) => k + 1));
  useEffect(() => {
    if (isNew || !canDokumente) return;
    let cancelled = false;
    dokumenteApi
      .getByBauvorhaben(Number(id))
      .then((response) => {
        if (!cancelled) setDokumente(response.data.data);
      })
      .catch(() => {
        if (!cancelled) setDokumente(null);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew, canDokumente, dokumenteReloadKey]);

  // Gewerk-Checkliste laden (nur im Detail-Modus)
  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    bauvorhabenApi
      .getGewerke(Number(id))
      .then((response) => {
        if (!cancelled) setAccordion(response.data.data);
      })
      .catch(() => {
        if (!cancelled) setAccordion(null);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isNew]);

  // Benutzerliste für den Bauleiter-Select — nötig im Anlege- und Bearbeiten-Modus
  const needsUsers = isNew || editOpen;
  useEffect(() => {
    if (!needsUsers || !isApprover || users.length > 0) return;
    adminApi
      .getUsers()
      .then((response) => setUsers(response.data.data))
      .catch(() => setUsers([]));
  }, [needsUsers, isApprover, users.length]);

  const handleSave = () => {
    setSaveError('');
    if (!formData.name.trim()) {
      setSaveError('Bitte einen Namen angeben');
      return;
    }
    setIsSaving(true);
    const payload = {
      name: formData.name.trim(),
      project_number: formData.project_number.trim() || null,
      street: formData.street.trim() || null,
      postal_code: formData.postal_code.trim() || null,
      city: formData.city.trim() || null,
      abgebrochen: formData.abgebrochen,
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
      bauleiter_user_id: formData.bauleiter_user_id ? Number(formData.bauleiter_user_id) : null,
      vertriebler_contact_id: vertriebler?.id ?? null,
      notes: formData.notes.trim() || null,
    };
    const request = isNew
      ? bauvorhabenApi.create(payload)
      : bauvorhabenApi.update(Number(id), payload);

    request
      .then((response) => {
        // Gemountete Listen-Spalte still nachladen (Create erscheint sofort).
        emitAppEvent('bauvorhaben:changed');
        if (isNew) {
          // Gleiche Layoutroute, neuer :id — Element bleibt gemountet,
          // loadDetail feuert über den id-Wechsel neu.
          navigate(`/bauvorhaben/${(response.data.data as { id: number }).id}`, { replace: true });
        } else {
          const created = (response.data.data as { address_proposals_created?: number }).address_proposals_created;
          if (created && created > 0) {
            setAssociationInfo(
              `Für ${created} Bauherr(en) wurde ein Antrag zur Übernahme der Bauvorhaben-Adresse erstellt — er muss noch genehmigt werden.`
            );
          }
          navigate(`/bauvorhaben/${id}`);
          loadDetail();
        }
      })
      .catch((err) => {
        setSaveError(err.response?.data?.error || 'Fehler beim Speichern');
      })
      .finally(() => setIsSaving(false));
  };

  const handleCancel = () => {
    if (isNew) {
      navigate('/bauvorhaben');
    } else {
      navigate(`/bauvorhaben/${id}`);
      loadDetail();
    }
  };

  const handleDelete = () => {
    if (!window.confirm('Möchten Sie dieses Bauvorhaben wirklich löschen?')) return;
    bauvorhabenApi
      .remove(Number(id))
      .then(() => {
        emitAppEvent('bauvorhaben:changed');
        navigate('/bauvorhaben');
      })
      .catch((err) => setError(err.response?.data?.error || 'Fehler beim Löschen'));
  };

  const handleAddContact = (role: BauvorhabenContactRole, contact: Contact) => {
    if (isNew) return;
    setAssociationError('');
    setAssociationInfo('');
    bauvorhabenApi
      .addContact(Number(id), {
        contact_id: contact.id,
        role,
      })
      .then((res) => {
        const created = res.data.data.address_proposals_created;
        if (created && created > 0) {
          setAssociationInfo(
            `Für ${created} Bauherr(en) wurde ein Antrag zur Übernahme der Bauvorhaben-Adresse erstellt — er muss noch genehmigt werden.`
          );
        }
        // Bauherren-Zähler in der Listen-Spalte mitziehen.
        emitAppEvent('bauvorhaben:changed');
        loadDetail();
      })
      .catch((err) => {
        setAssociationError(err.response?.data?.error || 'Fehler beim Zuordnen');
      });
  };

  const handleRemoveContact = (linkId: number) => {
    if (isNew) return;
    setAssociationError('');
    bauvorhabenApi
      .removeContact(Number(id), linkId)
      .then(() => {
        emitAppEvent('bauvorhaben:changed');
        loadDetail();
      })
      .catch((err) => {
        setAssociationError(err.response?.data?.error || 'Fehler beim Entfernen');
      });
  };

  // Gewerk-Status setzen: optimistisch im Akkordeon-State, Reload bei Fehler
  const handleGewerkStatusChange = async (gewerkId: number, status: GewerkStatus) => {
    if (isNew || !accordion) return;
    const previous = accordion;
    const updated: AccordionPayload = {
      ...accordion,
      phases: accordion.phases.map((phase) => ({
        ...phase,
        gewerke: phase.gewerke.map((gewerk) =>
          gewerk.id === gewerkId ? { ...gewerk, status } : gewerk
        ),
      })),
    };
    setAccordion(updated);
    try {
      await bauvorhabenApi.updateGewerkStatus(Number(id), gewerkId, status);
      // Fortschrittsbalken/Status der Listen-Zeile mitziehen.
      emitAppEvent('bauvorhaben:changed');
    } catch (err) {
      setAccordion(previous);
      setAssociationError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Aktualisieren des Gewerk-Status');
    }
  };

  // Gewerk-Kontakte an/ab hängen: erzeugt nur bauvorhaben_gewerke-Zeilen
  // mit Default-Status 'zu_erledigen' — Fortschritt/Status in der Liste
  // bleiben unverändert, deshalb KEIN emitAppEvent hier.
  const handleGewerkAddContact = async (gewerkId: number, contact: Contact) => {
    if (isNew) return;
    setAssociationError('');
    try {
      const res = await bauvorhabenApi.addGewerkContact(Number(id), gewerkId, contact.id);
      const linkId = res.data.data.link_id;
      setAccordion((prev) => prev && ({
        ...prev,
        phases: prev.phases.map((phase) => ({
          ...phase,
          gewerke: phase.gewerke.map((gewerk) =>
            gewerk.id === gewerkId
              ? {
                  ...gewerk,
                  contacts: [
                    ...gewerk.contacts,
                    {
                      link_id: linkId,
                      contact_id: contact.id,
                      display_name: contact.display_name,
                      first_name: contact.first_name,
                      last_name: contact.last_name,
                      company_name: contact.company_name,
                      email_address: contact.email_address,
                      business_phone: contact.business_phone,
                    },
                  ],
                }
              : gewerk
          ),
        })),
      }));
    } catch (err) {
      setAssociationError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Zuordnen');
    }
  };

  const handleGewerkRemoveContact = async (gewerkId: number, linkId: number) => {
    if (isNew) return;
    setAssociationError('');
    try {
      await bauvorhabenApi.removeGewerkContact(Number(id), gewerkId, linkId);
      setAccordion((prev) => prev && ({
        ...prev,
        phases: prev.phases.map((phase) => ({
          ...phase,
          gewerke: phase.gewerke.map((gewerk) =>
            gewerk.id === gewerkId
              ? { ...gewerk, contacts: gewerk.contacts.filter((c) => c.link_id !== linkId) }
              : gewerk
          ),
        })),
      }));
    } catch (err) {
      setAssociationError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Entfernen');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error && !bauvorhaben && !isNew) {
    return (
      <div className="p-4">
        <div className="card p-8 text-center">
          <AlertCircle className="mx-auto mb-3 h-8 w-8 text-red-500" />
          <p className="text-gray-700 mb-4">{error}</p>
          <Link to="/bauvorhaben" className="btn btn-secondary inline-flex items-center gap-2">
            <ArrowLeft className="h-4 w-4" />
            Zurück zur Übersicht
          </Link>
        </div>
      </div>
    );
  }

  const context: BauvorhabenDetailContext = {
    bauvorhaben,
    contacts,
    accordion,
    rechnungen,
    dokumente,
    canDokumente,
    reloadDokumente: () => setDokumenteReloadKey((k) => k + 1),
    formData,
    setFormData,
    vertriebler,
    setVertriebler,
    users,
    isApprover,
    isNew,
    isSaving,
    saveError,
    associationError,
    associationInfo,
    save: handleSave,
    cancel: handleCancel,
    remove: handleDelete,
    addContact: handleAddContact,
    removeContact: handleRemoveContact,
    changeGewerkStatus: handleGewerkStatusChange,
    addGewerkContact: handleGewerkAddContact,
    removeGewerkContact: handleGewerkRemoveContact,
    openContactFromLink,
    reload: loadDetail,
  };

  // Anlegen: nur das Formular in der Detail-Spalte
  if (isNew) {
    return (
      <div className="p-4">
        <Link to="/bauvorhaben" className="mb-4 inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800">
          <ArrowLeft className="h-4 w-4" />
          Bauvorhaben
        </Link>
        <div className="card p-6">
          <BauvorhabenEditForm context={context} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-col lg:flex-row">
      {/* Leseansicht */}
      <section className="min-w-0 flex-1 p-4">
        <BauvorhabenReadView context={context} />
      </section>
      {/* Bearbeiten-Spalte (Route /bauvorhaben/:id/edit) */}
      <section
        className={
          editOpen
            ? 'min-w-0 shrink-0 p-4 lg:w-[440px] lg:border-l lg:border-gray-200 lg:pl-6'
            : 'hidden'
        }
      >
        <Outlet context={context} />
      </section>
    </div>
  );
}